import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle, UserPlus, Users } from 'lucide-react';
import { logger } from '@/features/help/utils/logger';
import { SignersModal } from './SignersModal';

import { signerApi } from '../../api/signer.api';
import { useSubmissionComplete } from '../../hooks/use-submission-complete.hook';

// Purpose: Displays the success page after form submission and prevents navigation back to form
export const SubmissionComplete = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const [showSignersModal, setShowSignersModal] = useState(false);

  // Extract recordId and mappingId from URL params
  const { userShortId, recordId, mappingShortId, signerShortId } = params;
  
  // Check if this is a PDF-only submission
  const isPdfOnly = searchParams.get('pdfOnly') === '1';

  // Use custom hook for functionality (includes navigation prevention and signer data fetching)
  const { allSigners, isLoading, signersSubmitted, setSignersSubmitted, showInviteButton } =
    useSubmissionComplete({
      userShortId,
      recordId,
      mappingShortId,
      signerShortId,
    });

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-8">
      <div className="text-center max-w-lg bg-white p-8 rounded-lg shadow-lg">
        <CheckCircle className="mx-auto h-16 w-16 text-green-500 mb-6" />

        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          Application Submitted Successfully!
        </h1>

        <p className="text-gray-600 mb-8">
          Thank you for completing your application. We will review your information and contact you
          shortly with the next steps.
        </p>

        {/* Additional Signers Button or Success Message - Hidden for PDF-only submissions */}
        {!isPdfOnly && userShortId && recordId && mappingShortId && signerShortId && (
          <div className="mb-8">
            {isLoading ? (
              <div className="flex items-center justify-center text-gray-500">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
                <span>Loading...</span>
              </div>
            ) : showInviteButton && !signersSubmitted ? (
              <button
                onClick={() => setShowSignersModal(true)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Invite Additional Signers
              </button>
            ) : signersSubmitted ? (
              <div className="flex items-center justify-center text-green-600 bg-green-50 rounded-lg p-4">
                <Users className="w-5 h-5 mr-2" />
                <span className="font-medium">
                  Additional signers have been successfully invited!
                </span>
              </div>
            ) : null}
          </div>
        )}

        <div className="border-t border-gray-200 pt-6 mt-6">
          <p className="text-sm text-gray-500">
            You can now close this window. If you have any questions, please contact our support
            team.
          </p>
        </div>
      </div>

      {/* Signers Modal - Hidden for PDF-only submissions */}
      {!isPdfOnly && showSignersModal && userShortId && recordId && mappingShortId && signerShortId && (
        <SignersModal
          isOpen={showSignersModal}
          userShortId={userShortId}
          mappingShortId={mappingShortId}
          recordId={recordId}
          initialSigners={allSigners}
          onClose={() => setShowSignersModal(false)}
          onConfirm={async () => {
            if (userShortId && recordId && mappingShortId) {
              try {
                await signerApi.notifyAdditionalSigners(userShortId, recordId, mappingShortId);
                setSignersSubmitted(true);
              } catch (error) {
                logger.error('SubmissionComplete', 'Failed to notify additional signers', {
                  error,
                });
              } finally {
                setShowSignersModal(false);
              }
            }
          }}
        />
      )}
    </div>
  );
};
